import { map, mergeMap, catchError, tap, filter } from 'rxjs/operators'
import { of } from 'rxjs'
import { ofType, combineEpics } from 'redux-observable'

import { logger } from '../../util/log.helpers'
import { CAMPAIGN_ACTION, DOWNLOAD_ACTION } from '../../constants/actions'
import apiService from '../../services/api'
import { wrapUserAccessToken } from '../../util/auth.helpers'
import { commonParser } from '../../util/apiParser.helpers'
import { extractBrandId } from '../../util/epics.helpers'
import { API_PATHS } from '../../constants/api'
import {
  buildCampaignPaginationFilters,
  buildCodesDownloadForReportEmail,
  campaignCreateFieldsFormat,
  campaignDataObjFormat
} from '../../util/campaign.helpers'
import { toUrlQueryParams } from '../../util/object.helpers'
import { CAMPAIGN_LIST_PROFILE_DISPATCH } from '../reducers/campaign/campaignList'
import { DOWNLOAD_REPORT_TYPES } from '../../constants/app'

const handleCreateCampaignEpic = (action$, state$) =>
  action$.pipe(
    ofType(CAMPAIGN_ACTION.ON_CREATE_CAMPAIGN),
    mergeMap(() =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .createBrandResource(
            API_PATHS.CAMPAIGNS,
            extractBrandId(state$),
            campaignCreateFieldsFormat(state$),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => {
              return {
                type: CAMPAIGN_ACTION.ON_CREATE_CAMPAIGN_SUCCESS,
                payload: commonParser(response.data)
              }
            }),
            catchError((error) =>
              of({
                type: CAMPAIGN_ACTION.ON_CREATE_CAMPAIGN_FAILED,
                payload: error
              })
            )
          )
      )
    )
  )

const handleToggleCampaignEpic = (action$) =>
  action$.pipe(
    ofType(CAMPAIGN_ACTION.ON_TOGGLE_CAMPAIGN),
    map(({ payload }) => ({
      type: CAMPAIGN_ACTION.ON_UPDATE_CAMPAIGN,
      payload
    }))
  )

const handleUpdateCampaignEpic = (action$, state$) =>
  action$.pipe(
    ofType(CAMPAIGN_ACTION.ON_UPDATE_CAMPAIGN),
    mergeMap(
      ({
        payload: {
          campaign: { id, data }
        }
      }) =>
        wrapUserAccessToken((accessToken) =>
          apiService
            .updateBrandResource(
              API_PATHS.CAMPAIGNS,
              extractBrandId(state$),
              id,
              campaignDataObjFormat(data),
              accessToken
            )
            .pipe(
              tap((data) => logger(data)),
              map(({ response }) => {
                return {
                  type: CAMPAIGN_ACTION.ON_UPDATE_CAMPAIGN_SUCCESS,
                  payload: commonParser(response.data)
                }
              }),
              catchError((error) =>
                of({
                  type: CAMPAIGN_ACTION.ON_UPDATE_CAMPAIGN_FAILED,
                  payload: error
                })
              )
            )
        )
    )
  )

const handleGetCampaignsOnStatusChangeEpic = (action$) =>
  action$.pipe(
    ofType(CAMPAIGN_ACTION.CHANGE_LIST_STATE),
    filter(
      ({ payload: { property } }) =>
        property === CAMPAIGN_LIST_PROFILE_DISPATCH.STATUS
    ),
    mergeMap(() => {
      return [
        { type: CAMPAIGN_ACTION.ON_GET_CAMPAIGNS, payload: { pageNumber: 0 } }
      ]
    })
  )

const handleGetCampaignsOnCreateSuccessEpic = (action$) =>
  action$.pipe(
    ofType(CAMPAIGN_ACTION.ON_CREATE_CAMPAIGN_SUCCESS),
    mergeMap(() => {
      return [
        { type: CAMPAIGN_ACTION.ON_GET_CAMPAIGNS, payload: { pageNumber: 0 } }
      ]
    })
  )

const handleGetCampaignsEpic = (action$, state$) =>
  action$.pipe(
    ofType(CAMPAIGN_ACTION.ON_GET_CAMPAIGNS),
    map(({ payload }) => ({
      brandId: extractBrandId(state$),
      payload
    })),
    filter(({ brandId }) => brandId),
    mergeMap(({ brandId, payload }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .getBrandResources(
            `${API_PATHS.CAMPAIGNS}?${toUrlQueryParams(
              buildCampaignPaginationFilters(payload, state$)
            )}`,
            brandId,
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => {
              return {
                type: CAMPAIGN_ACTION.ON_GET_CAMPAIGNS_SUCCESS,
                payload: commonParser(response.data)
              }
            }),
            catchError(() =>
              of({
                type: CAMPAIGN_ACTION.ON_GET_CAMPAIGNS_FAILED
              })
            )
          )
      )
    )
  )

const handleCodesReportEmailEpic = (action$, state$) =>
  action$.pipe(
    ofType(CAMPAIGN_ACTION.ON_SEND_REPORT_IN_EMAIL),
    mergeMap(({ payload }) =>
      wrapUserAccessToken((accessToken) =>
        apiService
          .downloadReportToEmail(
            DOWNLOAD_REPORT_TYPES.CAMPAIGN_CODES,
            buildCodesDownloadForReportEmail(extractBrandId(state$), payload),
            accessToken
          )
          .pipe(
            tap((data) => logger(data)),
            map(({ response }) => ({
              type: DOWNLOAD_ACTION.ON_SENT_REPORT_IN_EMAIL_SUCCESS,
              payload: response
            })),
            catchError((err) =>
              of({
                type: DOWNLOAD_ACTION.ON_SENT_REPORT_IN_EMAIL_FAILED,
                payload: err
              })
            )
          )
      )
    )
  )

export const campaignEpic = combineEpics(
  handleCreateCampaignEpic,
  handleToggleCampaignEpic,
  handleUpdateCampaignEpic,
  handleGetCampaignsOnStatusChangeEpic,
  handleGetCampaignsOnCreateSuccessEpic,
  handleGetCampaignsEpic,
  handleCodesReportEmailEpic
)
